import consultationPreprocessingRequestsApi from "@/vue/api/consultation_preprocessing_requests";
import * as mutationTypes from "../mutation-types";

// =================================
// State Interfaces
// =================================
export interface ConsultationExtractedQuestionItem {
  id: number;
  question: string;
  quote: string | null;
}

export interface ConsultationPreprocessingRequestState {
  isComplete: boolean | null;
  questions: ConsultationExtractedQuestionItem[];
  status: ConsultationPreprocessingStatus;
}

export type ConsultationPreprocessingStatus =
  | "idle"
  | "polling"
  | "fetching"
  | "completed"
  | "failed";

// =================================
// API Response Interfaces
// =================================
export interface ConsultationPreprocessingRequestResponse {
  isComplete: boolean;
  questions: ConsultationExtractedQuestionItem[];
}

export interface FetchParams {
  consultationId?: number | string;
}

type StoreCommit = (type: string, payload?: unknown) => void;

type ConsultationPreprocessingActionContext = {
  commit: StoreCommit;
};

// =================================
// 初期State
// =================================
const initialState = (): ConsultationPreprocessingRequestState => ({
  isComplete: null,
  questions: [],
  status: "idle",
});

// =================================
// Storeの定義
// =================================
// -------------
// state
// -------------
const state: ConsultationPreprocessingRequestState = initialState();

// -------------
// getters
// -------------
const getters = {
  consultationExtractedQuestions: (state: ConsultationPreprocessingRequestState): ConsultationExtractedQuestionItem[] =>
    state.questions,
  consultationPreprocessingStatus: (state: ConsultationPreprocessingRequestState): ConsultationPreprocessingStatus =>
    state.status,
  hasExtractedQuestions: (state: ConsultationPreprocessingRequestState): boolean =>
    state.status === "completed" && state.questions.length > 0,
};

// -------------
// mutations
// -------------
const mutations = {
  [mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_REQUEST](
    state: ConsultationPreprocessingRequestState,
    payload: ConsultationPreprocessingRequestResponse
  ) {
    state.isComplete = payload.isComplete;
    state.questions = [...payload.questions];
  },
  [mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS](
    state: ConsultationPreprocessingRequestState,
    status: ConsultationPreprocessingStatus
  ) {
    state.status = status;
  },
  [mutationTypes.RESET_CONSULTATION_PREPROCESSING_REQUEST](
    state: ConsultationPreprocessingRequestState
  ) {
    Object.assign(state, initialState());
  },
};

// =================================
// actions
// =================================
const fetchConsultationPreprocessingRequest = async (
  { commit }: ConsultationPreprocessingActionContext,
  { consultationId }: FetchParams
): Promise<ConsultationPreprocessingRequestResponse | null> => {
  if (!consultationId) return null;

  commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "fetching");
  try {
    const response: ConsultationPreprocessingRequestResponse | undefined =
      await consultationPreprocessingRequestsApi.fetch({ consultationId });
    if (!response) {
      commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "failed");
      return null;
    }

    commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_REQUEST, response);
    if (response.isComplete) {
      commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "completed");
    } else {
      commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "polling");
    }
    return response;
  } catch (error) {
    console.error("[ConsultationPreprocessingRequest] fetch failed", error);
    commit(mutationTypes.ASSIGN_CONSULTATION_PREPROCESSING_STATUS, "failed");
    return null;
  }
};

const resetConsultationPreprocessingRequest = ({ commit }: ConsultationPreprocessingActionContext) => {
  commit(mutationTypes.RESET_CONSULTATION_PREPROCESSING_REQUEST);
};

const actions = {
  fetchConsultationPreprocessingRequest,
  resetConsultationPreprocessingRequest,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
